// API + types for the Interviewer Coach surface — the post-interview counterpart to Prep. Mirrors
// the backend InterviewCoachNote / InterviewerAssistantMessage(kind: coach) shape served at
// /api_ats/v1/ats_interviews/:id/coach_note | coach_messages.

import { http } from '@/lib/ats-https';

// `preparing` is an API-level status (not a DB enum): the recording is still transcribing, so there
// is no note row yet but one is coming. Treated as non-terminal — the FE keeps polling.
export type CoachStatus =
  | 'preparing'
  | 'pending'
  | 'processing'
  | 'completed'
  | 'failed';
export type Severity = 'big_one' | 'standard';
export type ThreadKind = 'failure' | 'conflict' | 'tradeoff' | 'decision';
export type CoverageStatus = 'covered' | 'partial' | 'skipped';
export type Priority = 'must_cover' | 'core' | 'optional';
export type GroundedIn =
  | 'scorecard_question'
  | 'job_description'
  | 'candidate_background'
  | 'prior_round_gap';

export interface TimeSegment {
  segment: string;
  planned_mins: number;
  actual_mins: number;
  note: string;
}
export interface TimeAllocation {
  drift_note: string;
  segments: TimeSegment[];
}
export interface UnprobedClaim {
  timestamp: string;
  quote: string;
  suggested_probe: string;
}
export interface WentWell {
  title: string;
  body: string;
}
export interface ImprovementArea {
  title: string;
  severity: Severity;
  narrative: string;
  moment_timestamp: string;
  why_it_matters: string;
  next_time: string;
}
export interface DroppedThread {
  kind: ThreadKind;
  title: string;
  timestamp: string;
  quote: string;
  what_was_there: string;
}
export interface Coverage {
  area: string;
  priority: Priority;
  grounded_in: GroundedIn;
  // Verbatim source text this area maps to (scorecard question, JD line, …) — shown on hover.
  source_ref: string;
  status: CoverageStatus;
  validated: string;
  gap: string;
}
export interface OneHabit {
  title: string;
  body: string;
}
export interface CoachNoteSections {
  summary: string;
  time_allocation: TimeAllocation;
  unprobed_claims: UnprobedClaim[];
  what_went_well: WentWell[];
  improvement_areas: ImprovementArea[];
  dropped_threads: DroppedThread[];
  coverage: Coverage[];
  one_habit: OneHabit;
}
export interface CoachNote {
  id: number | null;
  status: CoachStatus;
  sections: CoachNoteSections | null;
  created_at: string | null;
  updated_at: string | null;
}
export interface CoachMessage {
  id: number;
  status: CoachStatus;
  query: string;
  response: string | null;
  progress?: string | null;
  // Up to four contextual follow-up questions the coach suggested for this turn (FE pills).
  followups?: string[];
  created_at: string;
}

type InterviewId = number | string;

export const getCoachNote = async (
  interviewId: InterviewId
): Promise<CoachNote> => {
  // Cache-bust: the note is polled while generating and mutates underneath us, so a browser-cached
  // GET would make the poll read stale data and never see the note land.
  const res = await http.get(`/ats_interviews/${interviewId}/coach_note`, {
    params: { _t: Date.now() },
  });
  return res.data.coach_note;
};

export const listCoachMessages = async (
  interviewId: InterviewId
): Promise<CoachMessage[]> => {
  const res = await http.get(`/ats_interviews/${interviewId}/coach_messages`);
  return res.data.coach_messages;
};

export const sendCoachMessage = async (
  interviewId: InterviewId,
  query: string
): Promise<CoachMessage> => {
  const res = await http.post(`/ats_interviews/${interviewId}/coach_messages`, {
    query,
  });
  return res.data.coach_message;
};

export const getCoachMessage = async (
  interviewId: InterviewId,
  id: number
): Promise<CoachMessage> => {
  const res = await http.get(
    `/ats_interviews/${interviewId}/coach_messages/${id}`
  );
  return res.data.coach_message;
};

export const retryCoachMessage = async (
  interviewId: InterviewId,
  id: number
): Promise<CoachMessage> => {
  const res = await http.post(
    `/ats_interviews/${interviewId}/coach_messages/${id}/retry`
  );
  return res.data.coach_message;
};

// The highlight-to-ask passage (or a clicked coverage area) rides inside the query as a leading
// Markdown blockquote — the backend reads it natively and it renders as a quote in the bubble.
export const composeQuery = (text: string, context?: string | null): string => {
  const body = text.trim() || 'Tell me more about this.';
  if (!context) return body;
  const quoted = context.trim().replace(/\n+/g, ' ');
  return `> ${quoted}\n\n${body}`;
};

// Inverse of composeQuery for display: split a stored query back into its highlighted context
// (the leading blockquote, if any) and the typed text.
export const splitQuery = (
  query: string
): { context?: string; text: string } => {
  const match = query.match(/^>\s?(.+?)\n\n([\s\S]*)$/);
  if (match) return { context: match[1].trim(), text: match[2].trim() };
  return { text: query };
};

export const isTerminal = (status?: CoachStatus): boolean =>
  status === 'completed' || status === 'failed';

// A note timestamp ("mm:ss", or "h:mm:ss" for long rounds) → seconds, for seeking the recording.
// Returns null for anything unparseable so callers can fall back to a non-clickable label.
export const parseTimestamp = (ts?: string | null): number | null => {
  if (!ts) return null;
  const parts = ts.trim().split(':').map(Number);
  if (parts.some((n) => Number.isNaN(n))) return null;
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  return null;
};

// Opens the moment player at a timestamp: (seconds, original "mm:ss" label, the clicked element's
// viewport rect for anchoring). Shared by the note's ▶ pills and inline chat timestamps.
export type SeekMoment = (seconds: number, ts: string, rect: DOMRect) => void;
